import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, switchMap, throwError } from 'rxjs';
import { AUTH_PATH } from '../constants';
import { IRefreshToken } from '../interfaces';
import { JwtModel } from '../models';
import { AuthenticationService, UserAccountService } from '../services';

/**
 * Interceptor that handles token refresh on 401 errors.
 */
export const refreshTokenInterceptor: HttpInterceptorFn = (request, next) => {
  const userAccountService = inject(UserAccountService);
  const authenticationService = inject(AuthenticationService);
  const refreshTokenUrl = request.url.endsWith(`/${AUTH_PATH}/refresh`);

  return next(request).pipe(
    catchError((error) => {
      // Only logout on authentication failures (401, 404 on refresh endpoint)
      // Do NOT logout on server errors (500, 502, etc.) as these are transient
      if (refreshTokenUrl && error instanceof HttpErrorResponse && [401, 404].includes(error.status)) {
        // If the error is due to an expired/invalid token, log the user out
        authenticationService.logout();
        return next(request);
      }

      if (error instanceof HttpErrorResponse && error.status === 401) {
        const refreshTokenObj: IRefreshToken = { refreshToken: userAccountService.userJWT()?.refreshToken || '' };
        const currentUser = userAccountService.userJWT();
        const isLoggedIn = !!currentUser?.refreshToken && !!currentUser?.accessToken;

        if (!isLoggedIn) {
          return next(request);
        }

        return authenticationService.refreshToken(refreshTokenObj).pipe(
          switchMap((newJWTsObj: JwtModel) => {
            const updatedToken: JwtModel = { accessToken: newJWTsObj.accessToken, refreshToken: newJWTsObj.refreshToken };
            userAccountService.setUserAccount(updatedToken);

            const updatedRequest = request.clone({
              withCredentials: true,
              setHeaders: {
                Authorization: `Bearer ${updatedToken.accessToken}`,
              },
            });
            return next(updatedRequest);
          }),
          catchError((error) => {
            // Do NOT logout on server errors (500, 502, etc.)
            // These are transient errors that may occur during backend restart
            // Only logout on authentication failures
            if (error instanceof HttpErrorResponse && error.status === 401) {
              // Genuine authentication failure - logout
              authenticationService.logout();
              return next(request);
            }

            return throwError(() => error);
          }),
        );
      }

      return throwError(() => error);
    }),
  );
};
